/**
 * Responsibility: the collection page's data, from Firestore, in the shape the
 * page already expects.
 *
 * **An adapter rather than a rewrite.** `CollectionPage` was built against the
 * hub's `CollectionOut`, and changing every component to a second shape would
 * be a large diff that proves nothing. This reads the same information out of
 * Firestore and hands back the same object, so the page keeps working when the
 * hub does not without knowing that is why.
 *
 * The hub is still asked, and still wins when it answers. It enriches - a
 * holding's live quote, a name resolved from the catalogue - and those are
 * things Firestore does not hold and cannot compute. Firestore is the floor,
 * not the ceiling: what you see when there is nothing better.
 *
 * Nothing here recomputes money. The totals come from the summary document the
 * hub writes, because doing the arithmetic again in JavaScript would mean a
 * second implementation in a language with no decimal type.
 */

import { useMemo } from 'react';

import type { Collection, CollectionItem, CollectionSummary } from '../client';
import {
  useLiveHistory,
  useLiveHoldings,
  useLiveSummary,
  type StoredHolding,
} from '../../firestore/useDurable';
import type { WithId } from '../../firestore/useLive';

/** A Firestore timestamp, however the SDK happened to hand it over. */
function asIso(value: unknown): string {
  if (value && typeof value === 'object' && 'seconds' in value) {
    const seconds = (value as { seconds: number }).seconds;
    return new Date(seconds * 1000).toISOString();
  }
  return typeof value === 'string' ? value : new Date(0).toISOString();
}

/**
 * A stored holding as the page's row type.
 *
 * `holding_value` and `holding_cost` are per-position - the unit figure times
 * the quantity - which is the same rule the hub applies. A collection of one
 * card times forty is not forty cards at one card's price.
 */
function toItem(row: WithId<StoredHolding>): CollectionItem {
  const quantity = row.quantity ?? 1;
  const unit = row.latest_value ?? null;
  const cost = row.cost_basis ?? null;
  const times = (amount: string | null): string | null =>
    amount === null ? null : String(Number(amount) * quantity);

  return {
    id: row.id,
    product_key: row.product_key,
    name: row.name,
    category: row.category,
    url: row.url,
    image_url: row.image_url ?? null,
    grading_company: row.grading_company ?? null,
    grade_label: row.grade_label ?? 'Ungraded',
    cert_number: row.cert_number ?? null,
    acquired_on: null,
    quantity,
    cost_basis: cost,
    latest_value: unit,
    holding_value: times(unit),
    holding_cost: times(cost),
    valued_at: null,
    notes: row.notes ?? '',
    added_at: asIso(row.added_at),
  } as unknown as CollectionItem;
}

const EMPTY_SUMMARY: CollectionSummary = {
  item_count: 0,
  card_count: 0,
  total_value: '0',
  total_cost: '0',
  total_gain: null,
  change_1d: null,
  change_30d: null,
  change_90d: null,
  tracked_days: 0,
} as unknown as CollectionSummary;

/**
 * The collection as Firestore holds it, or undefined until something arrives.
 *
 * Undefined rather than an empty collection while loading: an empty grid and a
 * grid that has not loaded look the same, and only one of them means somebody
 * owns nothing.
 */
export function useDurableCollection(): { data: Collection | undefined; stale: boolean } {
  const holdings = useLiveHoldings();
  const history = useLiveHistory();
  const summary = useLiveSummary();

  return useMemo(() => {
    if (!holdings.data) return { data: undefined, stale: holdings.stale };

    const series = (history.data ?? [])
      .map((row) => ({ observed_on: row.id, value: row.total }))
      .sort((a, b) => a.observed_on.localeCompare(b.observed_on));

    return {
      data: {
        items: holdings.data.map(toItem),
        history: series,
        summary: (summary.data ?? EMPTY_SUMMARY) as CollectionSummary,
      } as unknown as Collection,
      stale: holdings.stale || history.stale || summary.stale,
    };
  }, [holdings.data, holdings.stale, history.data, history.stale, summary.data, summary.stale]);
}
